'use client';

import { cn } from '@/lib/utils';

interface MobileAppBannerProps {
  className?: string;
}

export function MobileAppBanner({ className }: MobileAppBannerProps) {
  return (
    <section className={cn('relative mx-4 sm:mx-0', className)}>
      {/* Background card — скругление + overflow-hidden для фона */}
      <div
        className={cn(
          'absolute inset-0 rounded-3xl overflow-hidden',
          'bg-[var(--bg-elevated)] dark:bg-[var(--surface)]',
        )}
      >
        {/* Gradient overlay — inline rgba for old Safari compat */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(123, 66, 255, 0.25)' }} />
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full" style={{ backgroundColor: 'rgba(123, 66, 255, 0.05)' }} />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full" style={{ backgroundColor: 'rgba(123, 66, 255, 0.05)' }} />
      </div>

      {/* Content — без overflow-hidden, картинка может выпирать */}
      <div className="relative z-10 flex flex-col md:flex-row items-center gap-0 md:gap-8 px-6 pt-10 pb-0 md:px-10 md:pt-6 md:pb-0">
        {/* Text content */}
        <div className="flex-1 text-center md:text-left">
          <h2 className="text-2xl md:text-2xl lg:text-3xl font-bold leading-tight text-[var(--text-primary)]">
            Скачайте наше
            <span className="md:inline hidden"> </span>
            <br className="md:hidden" />
            мобильное приложение
          </h2>
          <p className="mt-2 text-sm max-w-md mx-auto md:mx-0 text-[var(--text-secondary)]">
            Заказывайте любимые блюда ещё быстрее, копите бонусы и получайте эксклюзивные предложения
          </p>

          {/* Store buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mt-4 md:mt-5">
            {/* Google Play */}
            <a
              href="https://play.google.com/store/apps/details?id=com.steepdesign.aziaapp"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-xl px-5 py-3 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto justify-center bg-[var(--primary)] hover:bg-[var(--primary-hover)]"
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7 text-white flex-shrink-0" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.196 12l2.502-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
              </svg>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider leading-none" style={{ color: 'rgba(255,255,255,0.6)' }}>Доступно в</div>
                <div className="text-sm font-semibold text-white leading-tight mt-0.5">Google Play</div>
              </div>
            </a>

            {/* Apple */}
            <a
              href="https://apps.apple.com/ru/app/азиаточка-доставка-еды/id6761508832"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-xl px-5 py-3 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto justify-center bg-[var(--surface-secondary)] hover:bg-[var(--surface-hover)]"
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0 text-[var(--text-primary)]" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider leading-none text-[var(--text-tertiary)]">Доступно в</div>
                <div className="text-sm font-semibold leading-tight mt-0.5 text-[var(--text-primary)]">App Store</div>
              </div>
            </a>
          </div>
        </div>

        {/* Phone image — desktop: справа, выпирает вверх за пределы блока */}
        <div className="flex-shrink-0 flex justify-center items-end mt-6 md:mt-0 md:self-end overflow-hidden md:overflow-visible">
          <img
            src="https://cdn.aziatochka.ru/aziatochka/Frame%202131328302-g5555555555555555555-low%20resolution-2x.webp"
            alt="Мобильное приложение Азиаточка"
            className="w-[200px] md:w-[300px] lg:w-[340px] object-contain md:-mt-12"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}
